/**
 * tokens.js — Single source of truth for the Artificers AI design token system.
 *
 * Consumed by:
 *   - tailwind.config.js   (the `theme.extend` object)
 *   - /styleguide dashboard (imported directly by StyleguidePage.jsx)
 *   - scripts/build-merlin-tokens.mjs  (generates tokens.css for the Merlin voice app)
 *
 * Rules:
 *   - All hex values are quoted strings (ds-lint bans unquoted hex in src/**\/*.js).
 *   - Do not add Tailwind-specific concepts here; keep this a plain data object.
 *   - When you add a colour, also update scripts/build-merlin-tokens.mjs if it
 *     should be surfaced as a CSS custom property.
 */

export const theme = {
  // ─── Colors ──────────────────────────────────────
  colors: {
    // Brand primaries
    brand: {
      blue: '#2d63c0',          // Primary signature
      'blue-bright': '#3B82F6', // Bright accent / glows
      'blue-pale': '#b4c8ff',   // Icon glyph on brand-gradient tiles
      purple: '#A78BFA',        // Pillar 02 / Train accent
    },
    // Dark surface scale
    ink: {
      50: '#f5f6fa',   // Foreground on dark        ~19:1 on surface
      100: '#e1e3eb',
      200: '#a7abb8',  // Muted on dark             ~8.7:1
      300: '#9fa3ac',  // Subtle on dark            ~7.3:1  (raised from previous value)
      900: '#070711',  // Base background
      950: '#050510',  // Deepest
    },
    surface: {
      DEFAULT: '#070711', // base dark
      alt: '#0b0b17',     // alternate band
      raised: '#111125',  // raised cards
      subtle: '#0a0a15',
    },
    terminal: {
      screen: '#070b14', // phosphor screen base — deep blue-black
      bar:    '#0c1322', // window header bar — slightly raised
    },
    feedback: {
      success: '#10b981',
      warn: '#f59e0b',
      danger: '#ee4444',
    },
    // Functional tokens for the Merlin voice widget
    merlin: {
      user: '#5dd3ff', // User-speaking indicator (cyan)
    },
  },

  // ─── Typography ──────────────────────────────────
  fontFamily: {
    display: ['Sora', 'system-ui', 'sans-serif'],
    sans: ['Inter', 'system-ui', 'sans-serif'],
    mono: ['"JetBrains Mono"', 'ui-monospace', 'monospace'],
  },
  fontSize: {
    // Display scale (Sora)
    'display-2xl': ['clamp(40px, 6vw, 76px)', { lineHeight: '1.05', letterSpacing: '-0.035em' }],
    'display-xl': ['clamp(34px, 5vw, 60px)', { lineHeight: '1.08', letterSpacing: '-0.035em' }],
    'display-lg': ['clamp(32px, 4vw, 52px)', { lineHeight: '1.1', letterSpacing: '-0.03em' }],
    'display-md': ['clamp(28px, 3.4vw, 44px)', { lineHeight: '1.15', letterSpacing: '-0.03em' }],
    'display-sm': ['22px', { lineHeight: '1.2', letterSpacing: '-0.02em' }],
    // Heading scale
    'h1': ['44px', { lineHeight: '1.15', letterSpacing: '-0.02em' }],
    'h2': ['32px', { lineHeight: '1.2', letterSpacing: '-0.02em' }],
    'h3': ['22px', { lineHeight: '1.25', letterSpacing: '-0.02em' }],
    'h4': ['18px', { lineHeight: '1.3', letterSpacing: '-0.015em' }],
    // Body
    'body-lg': ['17px', { lineHeight: '1.6' }],
    'body': ['15px', { lineHeight: '1.55' }],
    'body-sm': ['14px', { lineHeight: '1.55' }],
    // Bespoke display sizes
    'card-title': ['19px', { lineHeight: '1.3', letterSpacing: '-0.015em' }],
    'metric': ['44px', { lineHeight: '1', letterSpacing: '-0.03em' }],
    'stat': ['clamp(52px, 7vw, 64px)', { lineHeight: '1', letterSpacing: '-0.04em' }],
    // Mono / labels
    'mono-xs': ['11px', { lineHeight: '1.4', letterSpacing: '0.18em' }],
    'mono-sm': ['12px', { lineHeight: '1.4', letterSpacing: '0.14em' }],
    'mono-md': ['13px', { lineHeight: '1.5', letterSpacing: '0.05em' }],
  },

  // ─── Spacing & layout ────────────────────────────
  maxWidth: {
    page: '1200px',
    prose: '68ch',
  },
  spacing: {
    'section': 'clamp(72px, 10vw, 140px)',
  },

  // ─── Radii ───────────────────────────────────────
  borderRadius: {
    'sm': '6px',
    DEFAULT: '10px',
    'lg': '16px',
    'xl': '24px',
  },

  // ─── Shadows & glows ─────────────────────────────
  boxShadow: {
    'glow-blue': '0 8px 24px -10px rgba(59,130,246,.55)',
    'glow-blue-lg': '0 24px 60px -20px rgba(45,99,192,.55)',
    'glow-purple': '0 24px 60px -20px rgba(167,139,250,.4)',
    'spine-glow': '0 0 16px rgba(59,130,246,0.45)',
    'step-dot-glow': '0 0 8px rgba(59,130,246,0.6)',
    'step-ring': '0 0 0 6px rgba(59,130,246,0.14), 0 0 24px rgba(59,130,246,0.65)',
  },

  // ─── Background images / gradients ───────────────
  backgroundImage: {
    'brand-gradient': 'linear-gradient(135deg, #2d63c0 0%, #3B82F6 100%)', // ds-lint-allow
    'brand-gradient-purple': 'linear-gradient(135deg, #A78BFA 0%, #3B82F6 100%)', // ds-lint-allow
    'glass-card': 'linear-gradient(160deg, rgba(255,255,255,.035) 0%, rgba(255,255,255,.015) 100%)',
    'glass-card-hover': 'linear-gradient(160deg, rgba(255,255,255,.06) 0%, rgba(255,255,255,.02) 100%)',
    'bar-fill': 'linear-gradient(90deg, #2d63c0, #3B82F6)', // ds-lint-allow
  },

  // ─── Animation ───────────────────────────────────
  keyframes: {
    'fade-up': {
      from: { opacity: '0', transform: 'translateY(14px)' },
      to: { opacity: '1', transform: 'translateY(0)' },
    },
    'split-in': {
      from: { opacity: '0', transform: 'translateY(0.6em) rotate(2deg)' },
      to: { opacity: '1', transform: 'translateY(0) rotate(0)' },
    },
    'phase-in-left': {
      from: { opacity: '0', transform: 'translateX(-28px)' },
      to: { opacity: '1', transform: 'translateX(0)' },
    },
    'phase-in-right': {
      from: { opacity: '0', transform: 'translateX(28px)' },
      to: { opacity: '1', transform: 'translateX(0)' },
    },
    'spine-pulse': {
      '0%': { opacity: '0.7', transform: 'scale(1)' },
      '100%': { opacity: '0', transform: 'scale(3.2)' },
    },
  },
  animation: {
    'fade-up': 'fade-up 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) both',
    'split-in': 'split-in 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both',
    'phase-in-left': 'phase-in-left 0.8s cubic-bezier(0.16, 1, 0.3, 1) both',
    'phase-in-right': 'phase-in-right 0.8s cubic-bezier(0.16, 1, 0.3, 1) both',
    'spine-pulse': 'spine-pulse 2.4s cubic-bezier(0.16, 1, 0.3, 1) infinite',
  },

  // ─── Backdrop ────────────────────────────────────
  backdropBlur: {
    glass: '16px',
  },
};

/**
 * Merlin glass surface treatment.
 * Not Tailwind-exposed — used exclusively by build-merlin-tokens.mjs
 * to generate CSS custom properties for the Merlin voice app's chrome.
 */
export const merlinSurfaces = {
  glass: 'rgba(18, 24, 40, 0.55)',
  glassHi: 'rgba(30, 38, 60, 0.7)',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHi: 'rgba(255, 255, 255, 0.18)',
  shadow: '0 30px 80px -20px rgba(0, 0, 0, 0.8)',
};
